<template>
  <Panel name="filter-panel"
         collapses="left"
         :collapsed="filterPanelCollapsed">
    <template #panel-header>
      <FilterPanelHeader title="Filter" />
    </template>

    <template #panel-content>
      <div class="filter-panel-content container px-3 py-1"
           @click="showFilterPanel()">
        <div class="row mb-3 pt-1 pb-2 mx-0 fs-16 cruises-found">
          <div class="col-8 ps-0">
            Reisen gefunden:
          </div>
          <div class="col fw-bold color-expinet-blue text-end pe-0">
            <Price :amount="totalResults"
                   :dash="false"
                   :symbol="false" />
          </div>
        </div>

        <div class="d-flex">
          <DateField label="Anreise"
                     field-name="departureDate"
                     :field-value="getFilterFieldValue('departureDate')?.toString()"
                     :value-changed="setFilterFieldValue"
                     :clicked="setDepartureDatePickerOpen"
                     :close-picker="setDepartureDatePickerClose"
                     :picker-open="departureDatePickerOpen"
                     :close-handler="departureDatefieldCloseHandler">
            <DatePicker field-name="departureDate"
                        :field-value="getFilterFieldValue('departureDate')?.toString()"
                        :value-changed="setFilterFieldValue"
                        :min-date="minDepartureDate"
                        :max-date="maxDate"
                        :open="departureDatePickerOpen"
                        :picked="setDepartureDatePickerClose"
                        class="date-picker" />
          </DateField>

          <DateField label="Abreise"
                     field-name="arrivalDate"
                     :field-value="getFilterFieldValue('arrivalDate')?.toString()"
                     :value-changed="setFilterFieldValue"
                     :clicked="setArrivalDatePickerOpen"
                     :close-picker="setArrivalDatePickerClose"
                     :picker-open="arrivalDatePickerOpen"
                     :close-handler="arrivalDatefieldCloseHandler"
                     class="ms-3">
            <DatePicker field-name="arrivalDate"
                        :field-value="getFilterFieldValue('arrivalDate')?.toString()"
                        :value-changed="setFilterFieldValue"
                        :min-date="minArrivalDate"
                        :max-date="maxDate"
                        :open="arrivalDatePickerOpen"
                        :picked="setArrivalDatePickerClose"
                        class="date-picker" />
          </DateField>
        </div>

        <TagsField field-name="regions"
                   label="Zielgebiet"
                   placeholder="Alle"
                   :item-list="filterFieldDropDownData.regions"
                   :field-value="(getFilterFieldValue('regions') as string[])"
                   :value-changed="setFilterFieldValue" />


        <DropdownField field-name="adults"
                       label="Erwachsene (ab 25 Jahre)"
                       :item-list="adults"
                       :field-value="getFilterFieldValue('adults')?.toString()"
                       :value-changed="setFilterFieldValue"
                       class="mb-2" />

        <div class="d-flex mb-2">
          <DropdownField field-name="juveniles"
                         label="Jugendl. (16 bis 24 J.)"
                         :item-list="juveniles"
                         :field-value="getFilterFieldValue('juveniles')?.toString()"
                         :value-changed="setFilterFieldValue" />
          <DropdownField field-name="children"
                         label="Kinder (2 bis 15 J.)"
                         :item-list="children"
                         :field-value="getFilterFieldValue('children')?.toString()"
                         :value-changed="setFilterFieldValue"
                         class="ms-3" />
        </div>

        <div>
          <FieldLabel>Baby (6 bis 23 Monate bei Reisebeginn)</FieldLabel>
          <i ref="info-babies"
             class="fa fa-info-circle info-icon"
             @mouseover="displayTooltip('info-babies')"
             @mouseleave="hideTooltip('info-babies')" />
        </div>

        <Tooltip :open="tooltips['info-babies'].show"
                 :position="tooltips['info-babies'].pos"
                 orient="top">
          <span class="tooltip-text fw-normal">
            <ul class="m-0 ps-2">
              <li>Das Mindestalter für Babys beträgt 6 Monate bei Reiseantritt, bei Reisen mit 3 oder mehr
                aufeinanderfolgenden Seetagen 12 Monate.
              </li>
              <li>Für werdende Mütter, die sich bei Reiseende in der 24. Schwangerschaftswoche oder darüber hinaus befinden,
                <br>ist die Teilnahme an einer Reise aus Sicherheitsgründen nicht möglich.
              </li>
            </ul>
          </span>
        </Tooltip>

        <div class="d-flex">
          <DropdownField field-name="babies"
                         :item-list="babies"
                         :field-value="getFilterFieldValue('babies')?.toString()"
                         :value-changed="setFilterFieldValue" />
        </div>


        <ToggleButtonGroup label="Dauer"
                           field-name="duration"
                           :field-value="getFilterFieldValue('duration')"
                           :items="durations"
                           :value-changed="setFilterFieldValue"
                           class="mt-2" />

        <TagsField field-name="campaigns"
                   label="Aktuelle Aktionen"
                   placeholder="Alle"
                   :item-list="filterFieldDropDownData.campaigns"
                   :field-value="(getFilterFieldValue('campaigns') as string[])"
                   :value-changed="setFilterFieldValue" />

        <TagsField field-name="airports"
                   label="AIDA-Flug"
                   placeholder="Alle"
                   :item-list="filterFieldDropDownData.airports"
                   :field-value="(getFilterFieldValue('airports') as string[])"
                   :value-changed="setFilterFieldValue" />

        <checkbox label="nur höherwertige Flugklassen"
                  :checked="higherFlightClassesSelected"
                  :clicked="selectHigherFlightClasses" />

        <div class="d-flex mb-2">
          <checkbox label="nur Wunschflug"
                    :checked="promoFlightSelected"
                    :clicked="selectPromoFlight" />
          <i ref="info-flex-flights"
             class="ms-1 fa fa-info-circle info-icon"
             @mouseover="displayTooltip('info-flex-flights')"
             @mouseleave="hideTooltip('info-flex-flights')" />

          <Tooltip :open="tooltips['info-flex-flights'].show"
                   :position="tooltips['info-flex-flights'].pos">
            <span class="tooltip-text">
              <ul class="m-0 ps-2">
                <li>Nur Festbuchung möglich</li>
                <li>Umbuchung und Namensänderung ausgeschlossen</li>
                <li>Mindestens 20&thinsp;kg Freigepäck p. P. (Gewichtsgrenzen pro Gepäckstück gemäß Airline-Bestimmungen)</li>
                <li>Shuttle-Bus zum/vom Schiff im Preis enthalten</li>
              </ul>
            </span>
          </Tooltip>
        </div>

        <TagsField field-name="ports"
                   label="Wunschhafen"
                   placeholder="Alle"
                   :item-list="filterFieldDropDownDataSorted"
                   :field-value="(getFilterFieldValue('ports') as string[])"
                   :value-changed="setFilterFieldValue" />

        <div class="d-flex mb-2">
          <checkbox label="als Abfahrtshafen"
                    :checked="startPortSelected"
                    :clicked="selectStartPort"
                    :disabled="!filterFieldDropDownData.ports.length"
                    class="me-2" />
          <checkbox label="als Ankunftshafen"
                    :checked="endPortSelected"
                    :clicked="selectEndPort"
                    :disabled="!filterFieldDropDownData.ports.length" />
        </div>

        <TagsField field-name="categories"
                   label="Kabine"
                   placeholder="Alle"
                   :item-list="filterFieldDropDownData.categories"
                   :field-value="(getFilterFieldValue('categories') as string[])"
                   :value-changed="setFilterFieldValue" />

        <TagsField field-name="ships"
                   label="Schiff"
                   placeholder="Alle"
                   :item-list="filterFieldDropDownData.ships"
                   :field-value="(getFilterFieldValue('ships') as string[])"
                   :value-changed="setFilterFieldValue" />

        <TagsField field-name="priceModels"
                   label="Tarif"
                   placeholder="Alle"
                   :item-list="filterFieldDropDownData.priceModels"
                   :field-value="(getFilterFieldValue('priceModels') as string[])"
                   :value-changed="setFilterFieldValue" />

        <TagsField field-name="cruiseAttributes"
                   label="Erweitert"
                   placeholder="Alle"
                   :item-list="filterFieldDropDownData.cruiseAttributes"
                   :field-value="(getFilterFieldValue('cruiseAttributes') as string[])"
                   :value-changed="setFilterFieldValue" />

        <ExpiButton button-style="outline"
                    class="mt-4 mb-5"
                    button-color="red"
                    icon-classes="fa fa-trash"
                    :click="resetFilterSelectionAndRemoveQueryStrings"
                    label-id="Eingaben löschen" />
      </div>
    </template>
  </Panel>
</template>

<script lang="ts">
import { IDateFormat } from '@/general/interfaces'
import { useCruiseStore, useFilterStore, useUiStore } from '@/store'
import { useCatalogStore } from '@/store/catalog'
import { getFormattedDate } from '@/store/catalog/helper'
import { useCruiseListStore } from '@/store/cruise-list'
import { FieldValue, IFilterFields } from '@/store/filter/interface'
import { IPoint } from '@/store/ui/interfaces'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { cloneDeep } from 'lodash-es'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import { DEFAULT_DATE_FORMAT } from '../../general/helper'
import {
  adults,
  babies,
  children,
  durations,
  juveniles
} from '../../general/master-data.js'
import { getValidDateFormat } from '../../store/filter/validate'
import Checkbox from '../common/Checkbox.vue'
import DateField from '../common/DateField.vue'
import DatePicker from '../common/DatePicker.vue'
import DropdownField from '../common/DropdownField.vue'
import ExpiButton from '../common/ExpiButton.vue'
import FieldLabel from '../common/FieldLabel.vue'
import Panel from '../common/Panel.vue'
import Price from '../common/Price.vue'
import TagsField from '../common/TagsField.vue'
import ToggleButtonGroup from '../common/ToggleButtonGroup.vue'
import Tooltip from '../common/Tooltip.vue'
import { IDateField } from '../interfaces'
import FilterPanelHeader from './FilterPanelHeader.vue'

dayjs.locale('de')

export default defineComponent({
  components: {
    Checkbox,
    DateField,
    DatePicker,
    DropdownField,
    FilterPanelHeader,
    Panel,
    Price,
    ToggleButtonGroup,
    TagsField,
    ExpiButton,
    Tooltip,
    FieldLabel
  },

  data: () => {
    return {
      durations,
      adults,
      juveniles,
      children,
      babies,
      departureDatePickerOpen: false,
      arrivalDatePickerOpen: false,
    }
  },

  computed: {
    ...mapState(useUiStore, {
      filterPanelCollapsed: store => store.filterPanelCollapsed,
      tooltips: store => store.tooltips,
    }),
    ...mapWritableState(useUiStore, {
      filterPanelCollapsed: 'filterPanelCollapsed'
    }),
    ...mapState(useFilterStore, {
      filterFieldDropDownData: store => store.filterFieldDropDownData
    }),
    ...mapWritableState(useFilterStore, {
      filterFields: 'filterFields'
    }),
    ...mapWritableState(useCruiseListStore, {
      selectedEntryId: 'selectedEntryId',
    }),
    ...mapState(useCatalogStore, {
      totalResults: store => store.totalResults,
    }),
    ...mapWritableState(useCatalogStore, {
      requestId: 'requestId',
      pageNumber: 'pageNumber'
    }),

    minDepartureDate: () => {
      return dayjs().startOf('day').add(1, 'day')
    },
    maxDate: () => {
      return dayjs().startOf('day').add(3, 'year')
    },
    minArrivalDate() {
      const depDate = this.filterFields.departureDate.value
      let minDate = this.minDepartureDate.clone().add(1, 'day')

      if (!!depDate) {
        minDate = dayjs(depDate, DEFAULT_DATE_FORMAT).add(1, 'day')
      }

      return minDate
    },
    promoFlightSelected() {
      return !!this.getFilterFieldValue('hasFlexFlight')
    },

    startPortSelected() {
      return !!this.getFilterFieldValue('isStartPort')
    },

    endPortSelected() {
      return !!this.getFilterFieldValue('isEndPort')
    },

    higherFlightClassesSelected() {
      return !!this.getFilterFieldValue('hasHigherFlightClasses')
    },

    filterFieldDropDownDataSorted() {
      const ports = cloneDeep(this.filterFieldDropDownData.ports)
      ports.forEach(port => port.display = port.display.toUpperCase())
      return ports.sort(
        (port1, port2) => {
          return port1.display.localeCompare(port2.display, 'de')
        })
    }
  },

  methods: {
    ...mapActions(useUiStore, {
      hideTooltip: 'hideTooltip',
      hideDetailsPanel: 'hideDetailsPanel',
      showTooltip: 'showTooltip'
    }),
    ...mapActions(useFilterStore, {
      setFilter: 'setFilter',
      addFilterSelectTrackingEvent: 'addFilterSelectTrackingEvent',
      resetFilterSelection: 'resetFilterSelection',
      resetFilterSelectionAndRemoveQueryStrings: 'resetFilterSelectionAndRemoveQueryStrings'
    }),
    ...mapActions(useCatalogStore, {
      fetchCatalog: 'fetchCatalog'
    }),
    ...mapActions(useCruiseStore, {
      resetCruise: 'resetCruise'
    }),

    arrivalDatefieldCloseHandler(validDateFormat: IDateFormat[], defaultDateFormat: string) {
      const dateField: IDateField = {
        fieldValue: this.getFilterFieldValue('arrivalDate'),
        minDate: this.minArrivalDate,
        valueChanged: this.setFilterFieldValue,
        fieldName: 'arrivalDate',
        closePicker: this.setArrivalDatePickerClose,
      }

      this.dateFieldCloseHandler(dateField, validDateFormat, defaultDateFormat)
    },

    departureDatefieldCloseHandler(validDateFormat: IDateFormat[], defaultDateFormat: string) {
      const dateField: IDateField = {
        fieldValue: this.getFilterFieldValue('departureDate'),
        minDate: this.minDepartureDate,
        valueChanged: this.setFilterFieldValue,
        fieldName: 'departureDate',
        closePicker: this.setDepartureDatePickerClose,
      }

      this.dateFieldCloseHandler(dateField, validDateFormat, defaultDateFormat)
    },

    dateFieldCloseHandler: (dateField: IDateField, validDateFormat: IDateFormat[], defaultDateFormat: string) => {
      if (!!dateField.fieldValue) {
        let date

        for (const fmt of validDateFormat) {
          date = dayjs(dateField.fieldValue, fmt.format, true)

          if ((fmt.autoComplete && date.isBefore(dayjs())) || date.isBefore(dateField.minDate)) {
            while (date.isBefore(dateField.minDate)) {
              date = date.add(1, 'year')
            }
            break
          }

          if (date.isValid()) {
            break
          }
        }
        dateField.valueChanged(dateField.fieldName, date?.format(defaultDateFormat))
      }
      dateField.closePicker()
    },

    getFilterFieldValue(fieldName: keyof IFilterFields) {
      const filterValue = this.filterFields[fieldName].value

      switch (filterValue) {
        case 'true':
          return true
        case 'false':
          return false
        default:
          return filterValue
      }
    },

    setFilterFieldValue(fieldName: keyof IFilterFields, fieldValue: FieldValue) {
      if (!!fieldValue && this.filterFields[fieldName].type === 'date') {
        const validDateFormat = getValidDateFormat(fieldValue)

        if (!!validDateFormat) {
          const formattedFieldValue = getFormattedDate(fieldValue, validDateFormat.format)
          this.setFilter(fieldName, formattedFieldValue)

          if (fieldName === 'departureDate'
            && !!this.filterFields.arrivalDate.value
            && !dayjs(this.filterFields.arrivalDate.value, DEFAULT_DATE_FORMAT).isAfter(dayjs(formattedFieldValue, validDateFormat.format))
          ) {
            this.setFilter('arrivalDate', null)
          }
        }
        else if (fieldValue === '') {
          this.setFilter(fieldName, null)
        }
      } else {
        this.setFilter(fieldName, fieldValue)
      }

      this.resetFilterSelection()
    },

    setDepartureDatePickerOpen() {
      this.arrivalDatePickerOpen = false
      this.departureDatePickerOpen = true
    },

    setDepartureDatePickerClose() {
      this.departureDatePickerOpen = false
    },

    setArrivalDatePickerOpen() {
      this.departureDatePickerOpen = false
      this.arrivalDatePickerOpen = true
    },

    setArrivalDatePickerClose() {
      this.arrivalDatePickerOpen = false
    },

    selectPromoFlight() {
      this.setFilterFieldValue('hasFlexFlight', !this.getFilterFieldValue('hasFlexFlight'))
    },

    selectStartPort() {
      this.setFilterFieldValue('isStartPort', !this.getFilterFieldValue('isStartPort'))
    },

    selectEndPort() {
      this.setFilterFieldValue('isEndPort', !this.getFilterFieldValue('isEndPort'))
    },

    selectHigherFlightClasses() {
      this.setFilterFieldValue('hasHigherFlightClasses', !this.getFilterFieldValue('hasHigherFlightClasses'))
    },

    displayTooltip(name: string) {
      const refname = this.$refs[name] as HTMLElement

      if (!!refname) {
        const rect = refname.getBoundingClientRect()
        const pos: IPoint = { x: rect.left, y: rect.top }
        this.showTooltip({ name, pos })
      }
    },
    showFilterPanel() {
      this.filterPanelCollapsed = false
    },
  },
})
</script>

<style lang="scss" scoped>
@import '../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.scss';

.filter-panel-content {
  width: 295px;
}

.filter-panel-footer {
  height: 12px;
}

.info-icon {
  color: $color-expinet-blue;
  font-size: 16px;
  line-height: 20px;
  padding-top: 1px;
  position: relative;

  &:hover {
    color: $color-expinet-blue-h;
  }
}

.cruises-found {
  border-bottom: 2px solid $color-expinet-gray;
}

.tooltip-text {
  font-size: $default-font-size;
  line-height: 20px;
}

.date-picker {
  margin-top: -2px;
}
</style>